"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = exports.convertToStartEndOfDayInNewTimezone = void 0;
var _luxon = require("luxon");
var _react = require("react");
var _useRespondToTimezoneChange = _interopRequireDefault(require("./useRespondToTimezoneChange"));
var _TimezoneContext = require("../_contexts/TimezoneContext");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }
/**
 * This hook should be used when we want a datepicker to maintain the same
 * calendar day when the timezone changes.
 * To do this we convert the selected date to be the start/end of day in the
 * newly selected timezone.
 * This hook needs to be used at the highest level where we employ the timezone
 * context, because if the parent component re-renders we will be getting the
 * same value for the timezone ref used under the hood.
 * This is why this hook is not included in the DatePicker component directly:
 * one would  need to change the outside date, and just send ina new `value` prop
 * to the  DatePicker.
 */

/**
 * Helper function that converts a given date in the start or end of day in the
 * given (new) timezone, presuming a recent timezone change.
 * @param {object} - the args are
 *      date: {JS Date} date to be converted
 *      isEndOfDay: {bool}  wether date is end if day. When false date will be
 *                  start of day
 *      timezone: {string} the new timezone
 *      localTimezone: {string} in our apps we assume that a timezone is either
 *                     `UTC` or a `localTimezone` that we get from settings
 * @returns {JS Date} the converted date
 */
var convertToStartEndOfDayInNewTimezone = function convertToStartEndOfDayInNewTimezone(_ref) {
  var date = _ref.date,
    isEndOfDay = _ref.isEndOfDay,
    timezone = _ref.timezone,
    localTimezone = _ref.localTimezone;
  // first compute if there is a difference between localTimezone and UTC
  // if they are the same no transformations are needed
  if (localTimezone && _luxon.DateTime.fromJSDate(date).setZone(localTimezone).toFormat("yyyy-MM-dd HH:mm:ss") === _luxon.DateTime.fromJSDate(date).toUTC().toFormat("yyyy-MM-dd HH:mm:ss")) {
    return date;
  }
  var newDate = _luxon.DateTime.fromJSDate(date);
  if (timezone === "UTC") {
    // we changed from "local" timezone to UTC
    if (isEndOfDay) {
      // we have date as end of day
      // UTC is always before any other timezone so the calendar day in UTC will
      // be the same day in any other timezone
      newDate = newDate.setZone(timezone).endOf("day");
    } else {
      // we have date as start of day
      // in order to get the same calendar day in UTC, we'll want to get end
      // of day first and then transform to UTC timezone and then get the start o day
      newDate = newDate.endOf("day").setZone(timezone).startOf("day");
    }
  } else {
    // we changed from UTC to "local" timezone
    if (isEndOfDay) {
      // we have date as end of day in UTC
      // so in the local timezone this will be one calendar day ahead
      // so we need to transform to end of day and subtract one day
      newDate = newDate.setZone(timezone).endOf("day").minus({
        days: 1
      });
    } else {
      // we have date as start of day
      // UTC is always before any other timezone so the calendar day in UTC will
      // be the same day in any other timezone
      newDate = newDate.setZone(timezone).startOf("day");
    }
  }
  return newDate.toJSDate();
};

/**
 * @param {function} setDate - a use state setter, that will set the new date; it
 * needs to be from `useState` because it uses the previous state to do the
 * calculation.
 * @param {boolean} isEndOfDay - wether we want tho be converting the date to end
 * of day. By default it will convert to start of day.
 * @param {function} callback - an additional callback we want to call after setting
 * the interval. Will receive as argument the new interval value. Should be
 * memoized.
 */
exports.convertToStartEndOfDayInNewTimezone = convertToStartEndOfDayInNewTimezone;
var useDateRespondToTimezoneChange = function useDateRespondToTimezoneChange(setDate) {
  var isEndOfDay = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var callback = arguments.length > 2 ? arguments[2] : undefined;
  var _useTimezone = (0, _TimezoneContext.useTimezone)(),
    timezone = _useTimezone.timezone,
    localTimezone = _useTimezone.localTimezone;
  var setDateCallback = (0, _react.useCallback)(function () {
    return setDate(function (date) {
      // if the state was null it will stay null
      if (!date) {
        return null;
      }
      // get the new date
      var newJsDate = convertToStartEndOfDayInNewTimezone({
        date: date,
        timezone: timezone,
        isEndOfDay: isEndOfDay,
        localTimezone: localTimezone
      });
      callback === null || callback === void 0 ? void 0 : callback(newJsDate);
      return newJsDate;
    });
  }, [callback, isEndOfDay, localTimezone, timezone, setDate]);
  (0, _useRespondToTimezoneChange["default"])(setDateCallback);
};
var _default = useDateRespondToTimezoneChange;
exports["default"] = _default;